import type { FC } from 'react';
import { Button } from '@accure/ui';
import { ChevronRight } from 'lucide-react';

import { useAlertTypesData } from '@/api/looker/use-alert-types-data';
import { useListGroupsData } from '@/api/looker/use-list-groups-data';
import { FieldSelect } from '@/components/looker/field-select';
import { FieldSelectMulti } from '@/components/looker/field-select-multi';
import { Link } from '@/components/ui/link';
import { appConfig } from '@/config';
import { AlertStatus } from '@/entities/alert-status';
import { AlertType, alertTypeMapping } from '@/entities/alert-type';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useMe } from '@/hooks/use-me';
import type { AlertsFilters as Filters } from '@/types/alerts-filters';
import { plural } from '@/utils/format/label';

export type AlertsFiltersProps = {
  filters: Filters;
  onChange: (set: (oldFilters: Filters) => Filters) => void;
};

export const AlertsFilters: FC<AlertsFiltersProps> = ({
  filters,
  onChange,
}) => {
  const me = useMe();
  const { assetId } = useCurrentAsset();
  const sites = useListGroupsData();
  const alertTypes = useAlertTypesData();

  const availableAlertTypes =
    alertTypes.data?.alert_types
      ?.map((value) => ({
        value,
        label: alertTypeMapping[value as AlertType] || value,
      }))
      .filter((item) => item.label)
      .sort((a, b) => (a.label || '').localeCompare(b.label || '')) ?? [];

  function updateChangedSinceDays(changedSinceDays: number | undefined) {
    onChange((oldFilters) => ({
      ...oldFilters,
      changedSinceDays:
        changedSinceDays ?? appConfig.defaultAlertsFilters.changedSinceDays,
    }));
  }

  function updateGroupIds(groupIds: string[] | undefined) {
    onChange((oldFilters) => ({
      ...oldFilters,
      groupIds: groupIds ?? appConfig.defaultAlertsFilters.groupIds,
    }));
  }

  function updateAlertStatuses(alertStatuses: AlertStatus[] | undefined) {
    onChange((oldFilters) => {
      return {
        ...oldFilters,
        alertStatusList:
          alertStatuses ?? appConfig.defaultAlertsFilters.alertStatusList,
      };
    });
  }

  function updateAlertType(alertTypeList: AlertType[]) {
    onChange((oldFilters) => {
      return {
        ...oldFilters,
        alertTypeList,
      };
    });
  }

  return (
    <div
      className="flex size-full flex-wrap items-end justify-between gap-y-4 border-b border-gray-100 bg-white p-4 xl:px-[3.75rem] xl:pb-4 xl:pt-5"
      data-testid="alerts-filters"
    >
      <div className="flex flex-wrap items-end gap-4">
        <FieldSelect
          data-testid="filter-changed-since"
          className="min-w-[150px] max-w-[150px]"
          options={[
            { value: '1', label: 'Last 24 Hours' },
            { value: '7', label: 'Last 7 Days' },
            { value: '14', label: 'Last 14 Days' },
            { value: '30', label: 'Last 30 Days' },
            { value: '90', label: 'Last 90 Days' },
            { value: '365', label: 'Last Year' },
          ]}
          type="number"
          label="Changed in"
          placeholder="Changed in"
          value={filters.changedSinceDays.toString()}
          onChange={(valueStr) => {
            const value = Number(valueStr);

            if (Number.isNaN(value)) {
              updateChangedSinceDays(undefined);
            } else {
              updateChangedSinceDays(value);
            }
          }}
        />
        {assetId ? null : (
          <FieldSelectMulti
            data-testid="filter-assets"
            className="min-w-[250px] max-w-[250px]"
            options={
              sites.data?.groups.map((group) => ({
                value: group.value,
                label: group.label,
              })) ?? []
            }
            values={filters.groupIds}
            label={plural(me.config.group.labels.name)}
            placeholder={
              filters.groupIds.length > 0
                ? ''
                : !sites.data
                  ? plural(me.config.group.labels.name)
                  : `${sites.data.groups
                      .slice(0, 2)
                      .map((group) => group.label)
                      .join(', ')}...`
            }
            isLoading={sites.isLoading}
            onChange={(siteIds) => updateGroupIds(siteIds)}
          />
        )}
        <FieldSelectMulti
          data-testid="filter-status"
          className="min-w-[360px] max-w-[360px]"
          options={
            Object.values(AlertStatus).map((value) => ({ value })) as {
              value: AlertStatus;
            }[]
          }
          values={filters.alertStatusList}
          label="Status"
          async
          placeholder={
            filters.alertStatusList.length > 0 ? '' : `Critical, Warning...`
          }
          onChange={(alertStatuses) =>
            updateAlertStatuses(alertStatuses as AlertStatus[] | undefined)
          }
        />
        <FieldSelectMulti
          data-testid="filter-alert-type"
          className="min-w-[280px] max-w-[280px]"
          options={availableAlertTypes}
          label="Alert type"
          placeholder="Alert type"
          values={filters.alertTypeList ?? []}
          isFilterable={true}
          isLoading={alertTypes.isLoading}
          onChange={(alertTypeList = []) => {
            updateAlertType(alertTypeList as AlertType[]);
          }}
        />
      </div>
      <Link
        data-testid="button-export-alerts"
        to={{
          pathname: '/alerts/export',
        }}
      >
        <Button variant="outline" size="lg">
          <span className="hidden mac14:inline">Export Alerts</span>
          <span className="inline mac14:hidden">Export</span>
          <ChevronRight />
        </Button>
      </Link>
    </div>
  );
};
